import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Copy, CheckCircle, Zap, CreditCard, Settings2, Send, Download, FileText } from "lucide-react";
import { toast } from "sonner";

function WebhookSection({ settings, onCopy }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Zap className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-base">Webhook Endpoint</CardTitle>
            <CardDescription>Copy this URL and paste it into Zapier as the destination endpoint</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-xs text-muted-foreground uppercase tracking-wide">Webhook URL</Label>
          <div className="flex gap-2 mt-1.5">
            <div className="flex-1 bg-muted rounded-lg px-4 py-2.5 font-mono text-sm break-all border" data-testid="webhook-url">
              {settings?.webhook_url}
            </div>
            <Button variant="outline" onClick={() => onCopy(settings?.webhook_url)} data-testid="settings-webhook-url-copy-button">
              <Copy className="w-4 h-4 mr-2" /> Copy
            </Button>
          </div>
        </div>
        <div className="bg-muted/50 rounded-lg px-3 py-2 text-xs text-muted-foreground">
          <p>In Zapier, use <strong>Webhooks by Zapier &rarr; POST</strong> and paste this URL. No authentication headers needed.</p>
        </div>
      </CardContent>
    </Card>
  );
}

function InvoiceDefaultsSection({ primaryEmail, setPrimaryEmail, secondaryEmail, setSecondaryEmail, percentage, setPercentage, termsDays, setTermsDays, onSave, saving }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-base">Invoice Defaults</CardTitle>
            <CardDescription>Default values for automatically generated invoices</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="primary-email">Primary Invoice Email</Label>
            <Input id="primary-email" value={primaryEmail} onChange={(e) => setPrimaryEmail(e.target.value)} className="mt-1.5" data-testid="settings-primary-email" />
          </div>
          <div>
            <Label htmlFor="secondary-email">Secondary Invoice Email (CC)</Label>
            <Input id="secondary-email" value={secondaryEmail} onChange={(e) => setSecondaryEmail(e.target.value)} className="mt-1.5" data-testid="settings-secondary-email" />
          </div>
          <div>
            <Label htmlFor="percentage">Revenue Split Percentage</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <Input id="percentage" type="number" min={1} max={100} value={percentage} onChange={(e) => setPercentage(parseInt(e.target.value) || 50)} className="w-24" data-testid="settings-percentage" />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>
          <div>
            <Label htmlFor="terms">Invoice Terms (days)</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <Input id="terms" type="number" min={1} max={90} value={termsDays} onChange={(e) => setTermsDays(parseInt(e.target.value) || 15)} className="w-24" data-testid="settings-terms" />
              <span className="text-sm text-muted-foreground">Net {termsDays}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border">
          <CreditCard className="w-4 h-4 text-teal-600" />
          <span className="text-sm font-medium">Payment Method: ACH Only</span>
          <span className="text-xs text-muted-foreground">(enforced at invoice level)</span>
        </div>
        <Button onClick={onSave} disabled={saving} data-testid="save-settings">
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </CardContent>
    </Card>
  );
}

function StripeSection({ settings, onCopy }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Settings2 className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-base">Stripe Connection</CardTitle>
            <CardDescription>Connection status and sync</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <CheckCircle className={`w-4 h-4 ${settings?.stripe_connected ? 'text-emerald-500' : 'text-rose-500'}`} />
          <span className="text-sm font-medium">{settings?.stripe_connected ? 'Connected' : 'Not Connected'}</span>
        </div>
        {settings?.stripe_customer_id && (
          <div className="text-sm">
            <span className="text-muted-foreground">Customer ID: </span>
            <span className="font-mono text-xs">{settings.stripe_customer_id}</span>
          </div>
        )}
        <div className="text-sm">
          <span className="text-muted-foreground">Stripe Webhook URL: </span>
          <span className="font-mono text-xs break-all">{settings?.stripe_webhook_url}</span>
          <Button variant="ghost" size="icon" className="h-6 w-6 ml-1" onClick={() => onCopy(settings?.stripe_webhook_url)}>
            <Copy className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [primaryEmail, setPrimaryEmail] = useState("");
  const [secondaryEmail, setSecondaryEmail] = useState("");
  const [percentage, setPercentage] = useState(50);
  const [termsDays, setTermsDays] = useState(15);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(res.data);
      setPrimaryEmail(res.data.primary_invoice_email || "");
      setSecondaryEmail(res.data.secondary_invoice_email || "");
      setPercentage(res.data.invoice_percentage || 50);
      setTermsDays(res.data.invoice_terms_days || 15);
    } catch (err) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, {
        primary_invoice_email: primaryEmail, secondary_invoice_email: secondaryEmail,
        invoice_percentage: percentage, invoice_terms_days: termsDays,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Settings saved");
      fetchSettings();
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/seed`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Seed data created: ${res.data.counts.dealers} dealers, ${res.data.counts.payments} payments, ${res.data.counts.invoices} invoices`);
    } catch (err) {
      toast.error("Failed to create seed data");
    } finally {
      setSeeding(false);
    }
  };

  const handleClearSeed = async () => {
    setClearing(true);
    try {
      await axios.post(`${API}/seed/clear`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Seed data cleared");
    } catch (err) {
      toast.error("Failed to clear seed data");
    } finally {
      setClearing(false);
    }
  };

  const handleTestWebhook = async () => {
    setTestingWebhook(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/webhooks/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setTestResult(res.data);
      toast.success(res.data.status === "success" ? "Test webhook processed!" : "Test webhook failed");
    } catch (err) {
      toast.error("Failed to send test webhook");
    } finally {
      setTestingWebhook(false);
    }
  };

  const [clearingAll, setClearingAll] = useState(false);
  const handleClearAll = async () => {
    setClearingAll(true);
    try {
      const res = await axios.post(`${API}/data/clear-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("All data cleared");
    } catch (err) {
      toast.error("Failed to clear data");
    } finally {
      setClearingAll(false);
    }
  };

  const handleExport = (type) => { window.open(`${API}/export/${type}`, "_blank"); };

  if (loading) return <div className="animate-pulse p-8">Loading settings...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="settings-title">Settings & Integrations</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure webhook, invoice defaults, and Stripe connection</p>
      </div>

      <WebhookSection settings={settings} onCopy={copyToClipboard} />

      <InvoiceDefaultsSection
        primaryEmail={primaryEmail} setPrimaryEmail={setPrimaryEmail}
        secondaryEmail={secondaryEmail} setSecondaryEmail={setSecondaryEmail}
        percentage={percentage} setPercentage={setPercentage}
        termsDays={termsDays} setTermsDays={setTermsDays}
        onSave={saveSettings} saving={saving}
      />

      <StripeSection settings={settings} onCopy={copyToClipboard} />

      {/* Data Management */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Data Management</CardTitle>
          <CardDescription>Manage demo data and clear the database</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleSeed} disabled={seeding} data-testid="seed-data">
              {seeding ? "Creating..." : "Create Seed Data"}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={clearing} data-testid="clear-seed-data">
                  {clearing ? "Clearing..." : "Clear Seed Data"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear Seed Data?</AlertDialogTitle>
                  <AlertDialogDescription>This will remove demo dealers, payments, invoices, and alerts only.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleClearSeed}>Clear Seed Data</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-destructive mb-2">Danger Zone</p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={clearingAll} data-testid="clear-all-data">
                  {clearingAll ? "Clearing..." : "Clear ALL Data"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear ALL Data?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete ALL dealers, payments, invoices, alerts, and logs. Your admin login and settings will be preserved. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleClearAll} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete Everything</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Test Webhook */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Send className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base">Test Webhook</CardTitle>
              <CardDescription>Send a test payload to verify the webhook endpoint works end-to-end</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={handleTestWebhook} disabled={testingWebhook} data-testid="test-webhook">
            <Send className="w-4 h-4 mr-2" />
            {testingWebhook ? "Sending..." : "Send Test Webhook"}
          </Button>
          <p className="text-xs text-muted-foreground">
            This creates a test dealer payment event, processes it through the full pipeline. Note: A real Stripe invoice will be created and sent.
          </p>
          {testResult && (
            <div className={`p-3 rounded-lg border text-sm ${testResult.status === "success" ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
              <p className="font-medium">{testResult.status === "success" ? "Test Successful!" : "Test Failed"}</p>
              {testResult.response_body && (
                <pre className="font-mono text-xs mt-2 whitespace-pre-wrap">{JSON.stringify(testResult.response_body, null, 2)}</pre>
              )}
              {testResult.message && <p className="text-xs mt-1">{testResult.message}</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* CSV Exports */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Download className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base">Data Exports</CardTitle>
              <CardDescription>Download CSV exports for reporting and reconciliation</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => handleExport("dealers")} data-testid="export-dealers">
              <FileText className="w-4 h-4 mr-2" /> Export Dealers
            </Button>
            <Button variant="outline" onClick={() => handleExport("invoices")} data-testid="export-invoices">
              <FileText className="w-4 h-4 mr-2" /> Export Invoices
            </Button>
            <Button variant="outline" onClick={() => handleExport("payments")} data-testid="export-payments">
              <FileText className="w-4 h-4 mr-2" /> Export Payments
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
